import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { apiGet } from "../api/client";
import { ErrorBanner, useApiError } from "../components/ErrorBanner";
import { PageHeader, StatTile, StatusChip, InfoTip, CopyButton, EmptyState } from "../components/ui/Primitives";
import DataTable, { type Column } from "../components/ui/DataTable";
import Drawer, { DetailList } from "../components/ui/Drawer";
import {
  fmtBytes,
  fmtCompact,
  fmtKst,
  fmtKstShort,
  fmtRelative,
  fmtValue,
  ageSeconds,
} from "../lib/format";
import {
  EVENT_TYPE_LABELS,
  FILING_CATEGORY_LABELS,
  FORM_LABELS,
  INSIDER_CODE_LABELS,
  JOB_STATE_LABELS,
  MAPPING_STATUS_LABELS,
  METRIC_LABELS,
  ORIGIN_LABELS,
  QUALITY_FLAG_LABELS,
  gateLabel,
  labelOf,
} from "../lib/labels";
import {
  Activity,
  AlertTriangle,
  Building2,
  CheckCircle2,
  Database,
  FileText,
  GitBranch,
  LineChart,
  RefreshCw,
  Users,
  XCircle,
} from "lucide-react";

type TabKey = "status" | "features" | "disclosures" | "pipeline";

interface SchedulerTask {
  key: string;
  last_attempt: string | null;
  last_success: string | null;
  last_status: string | null;
  consecutive_failures: number;
  skipped_cycles: number;
}

interface EndpointRow {
  endpoint: string;
  last_attempt: string | null;
  last_success: string | null;
  total: number;
  ok_count: number;
}

interface StatusPayload {
  scheduler: {
    universe?: { source?: string; symbols?: string[]; degraded_reason?: string | null };
    tasks?: Record<string, SchedulerTask>;
    market_data_enabled?: boolean;
    company_news_enabled?: boolean;
    outbox_enabled?: boolean;
    input_revision_enabled?: boolean;
    eod_enabled?: boolean;
    eod_done_sessions?: string[];
    enabled_endpoints?: string[];
    auth_cooldown_sec?: number;
    degraded?: boolean;
    degraded_reason?: string;
    updated_at?: string;
  } | null;
  database: {
    available?: boolean;
    error?: string;
    endpoints?: EndpointRow[];
    outbox?: { pending: number; failed: number; last_published?: string | null };
    score_jobs?: { pending: number; failed: number; scored: number };
    last_feature_observed_at?: string | null;
    raw_storage?: {
      total_bytes: number;
      rows: number;
      bytes_7d: number;
      projected_30d_bytes: number;
      first_seen?: string | null;
    };
  } | null;
  collected_at?: string;
}

interface CanaryPayload {
  available?: boolean;
  verdict?: "pass" | "fail" | "unknown";
  window_hours?: number;
  generated_at?: string;
  error?: string;
  raw?: { total: number; recent: number; auth_failures: number; transient: number };
  features?: { total: number; recent: number; instruments: number };
  news?: {
    outbox_pending: number;
    outbox_failed: number;
    outbox_published: number;
    jobs_pending: number;
    jobs_failed: number;
    jobs_scored: number;
    revisions: number;
    scored_news_recent: number;
  };
  core?: { md_ohlcv_lag_seconds: number | null; s1_equity: number | null; s1_position: string | null };
  checks?: Array<{ name: string; passed: boolean; detail: string }>;
}

interface Instrument {
  symbol: string;
  ticker_id?: number | null;
  display_name?: string | null;
  instrument_type?: string | null;
  exchange?: string | null;
  currency?: string | null;
  related_perp?: string | null;
  mapping_status?: string | null;
  updated_at?: string | null;
}

interface MetricInfo {
  metric: string;
  unit: string | null;
  observations: number;
  last_observed?: string | null;
}

interface CatalogPayload {
  available?: boolean;
  instruments?: Instrument[];
  metrics?: MetricInfo[];
}

interface FeatureRow {
  observation_id?: string;
  instrument: string;
  metric: string;
  value_float: number | null;
  value_numeric: number | null;
  unit: string | null;
  observed_at: string | null;
  field_as_of: string | null;
  field_as_of_raw?: string | null;
  endpoint?: string | null;
  origin?: string | null;
  quality_flags?: string[] | null;
}

interface TickerEventRow {
  event_id?: string;
  event_at?: string | null;
  expires_at?: string | null;
  symbol?: string | null;
  instrument?: string | null;
  event_type?: string | null;
  provider_event_id?: string | null;
  source_id?: string | null;
}

interface FilingRow {
  revision_id?: string;
  provider_filing_id?: string | null;
  symbol?: string | null;
  form?: string | null;
  category?: string | null;
  title?: string | null;
  is_content?: boolean | null;
  filed_at?: string | null;
  observed_at?: string | null;
}

interface InsiderRow {
  observation_id?: string;
  observed_at?: string | null;
  symbol?: string | null;
  person_name?: string | null;
  person_title?: string | null;
  transaction_date?: string | null;
  transaction_code?: string | null;
  signed_value?: number | null;
  currency?: string | null;
  disclosure_unknown?: boolean | null;
}

interface EventsPayload {
  events?: TickerEventRow[];
  filings?: FilingRow[];
  insider_trades?: InsiderRow[];
}

interface PipelineRow {
  event_id?: string | null;
  outbox_id?: number | null;
  input_revision?: number | null;
  state?: string | null;
  attempts?: number | null;
  last_error?: string | null;
  created_at?: string | null;
  published_at_utc?: string | null;
  updated_at?: string | null;
  next_attempt_at?: string | null;
  scorer_version?: string | null;
  revision?: number | null;
  title?: string | null;
  provider?: string | null;
  provider_news_id?: string | null;
  first_seen_at?: string | null;
  observed_at?: string | null;
  origin?: string | null;
  is_deleted?: boolean | null;
}

interface PipelinePayload {
  available?: boolean;
  error?: string;
  outbox?: PipelineRow[];
  jobs?: PipelineRow[];
  revisions?: PipelineRow[];
}

interface CollectionState {
  tone: "ok" | "warn" | "danger" | "neutral";
  text: string;
  detail: string;
}

function collectionState(status: StatusPayload | null): CollectionState {
  if (!status) return { tone: "warn", text: "상태 조회 불가", detail: "API 응답이 없습니다." };
  const scheduler = status.scheduler;
  const database = status.database;
  if (database && database.available === false) {
    return {
      tone: "danger",
      text: "스키마/DB 조회 불가",
      detail: database.error ?? "saveticker 테이블을 읽을 수 없습니다.",
    };
  }
  if (!scheduler) {
    return { tone: "warn", text: "수집기 미가동", detail: "스케줄러 스냅샷이 없습니다." };
  }
  if (!scheduler.market_data_enabled && !scheduler.company_news_enabled) {
    return { tone: "neutral", text: "수집 비활성", detail: "market/company flag가 모두 off입니다." };
  }
  if (scheduler.degraded) {
    return {
      tone: "danger",
      text: "설정 저하",
      detail: scheduler.degraded_reason ?? "degraded 상태",
    };
  }
  if (scheduler.universe?.degraded_reason) {
    return {
      tone: "warn",
      text: "Universe 저하",
      detail: scheduler.universe.degraded_reason,
    };
  }
  const tasks = Object.values(scheduler.tasks ?? {});
  if (tasks.length === 0) {
    return { tone: "warn", text: "수집 task 미초기화", detail: "등록된 수집 task가 없습니다." };
  }
  if (tasks.some((task) => task.last_status === "auth_required" || task.last_status === "forbidden")) {
    return { tone: "danger", text: "인증 필요", detail: "SaveTicker 자격을 갱신해야 합니다." };
  }
  if (tasks.every((task) => task.last_status === "empty")) {
    return { tone: "ok", text: "수집 정상 · 신규 없음", detail: "요청은 성공했고 새 데이터가 없습니다." };
  }
  const failing = tasks.filter(
    (task) => task.last_status && task.last_status !== "ok" && task.last_status !== "empty",
  );
  if (failing.length === tasks.length) {
    return { tone: "danger", text: "수집 실패", detail: `마지막 상태: ${failing[0]?.last_status}` };
  }
  if (failing.length > 0) {
    return {
      tone: "warn",
      text: "일부 수집",
      detail: `${failing.length}/${tasks.length} task 실패`,
    };
  }
  return { tone: "ok", text: "수집 정상", detail: "모든 task가 정상입니다." };
}

const ORIGIN_OPTIONS = ["live", "attachment", "all"];

export default function SaveTicker() {
  const [activeTab, setActiveTab] = useState<TabKey>("status");
  const { error, setError, wrap } = useApiError();

  const [status, setStatus] = useState<StatusPayload | null>(null);
  const [canary, setCanary] = useState<CanaryPayload | null>(null);
  const [catalog, setCatalog] = useState<CatalogPayload>({});
  const [statusLoading, setStatusLoading] = useState(true);

  const [featureSymbol, setFeatureSymbol] = useState("");
  const [featureMetric, setFeatureMetric] = useState("");
  const [featureOrigin, setFeatureOrigin] = useState("live");
  const [featureLimit, setFeatureLimit] = useState(200);
  const [features, setFeatures] = useState<FeatureRow[]>([]);
  const [featuresLoading, setFeaturesLoading] = useState(false);
  const [featureDetail, setFeatureDetail] = useState<FeatureRow | null>(null);

  const [eventSymbol, setEventSymbol] = useState("");
  const [includeHistory, setIncludeHistory] = useState(false);
  const [events, setEvents] = useState<EventsPayload | null>(null);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [filingDetail, setFilingDetail] = useState<FilingRow | null>(null);

  const [pipeline, setPipeline] = useState<PipelinePayload | null>(null);
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [onlyProblems, setOnlyProblems] = useState(false);
  const [pipelineDetail, setPipelineDetail] = useState<{ kind: string; row: PipelineRow } | null>(null);

  const fetchStatus = useCallback(async () => {
    const [statusRes, canaryRes, catalogRes] = await Promise.allSettled([
      apiGet<StatusPayload>("/saveticker/status"),
      apiGet<CanaryPayload>("/saveticker/canary?hours=24"),
      apiGet<CatalogPayload>("/saveticker/catalog"),
    ]);
    setStatus(statusRes.status === "fulfilled" ? statusRes.value : null);
    setCanary(canaryRes.status === "fulfilled" ? canaryRes.value : null);
    setCatalog(catalogRes.status === "fulfilled" ? catalogRes.value : {});
    setStatusLoading(false);
  }, []);

  useEffect(() => {
    wrap(fetchStatus);
    const timer = setInterval(() => void fetchStatus(), 60_000);
    return () => clearInterval(timer);
  }, [fetchStatus, wrap]);

  const fetchFeatures = useCallback(async () => {
    setFeaturesLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(featureLimit), origin: featureOrigin });
      if (featureSymbol) params.set("symbol", featureSymbol);
      if (featureMetric) params.set("metric", featureMetric);
      const data = await apiGet<{ values: FeatureRow[] }>(`/saveticker/features?${params.toString()}`);
      setFeatures(Array.isArray(data.values) ? data.values : []);
    } catch (err) {
      setError(String(err));
      setFeatures([]);
    } finally {
      setFeaturesLoading(false);
    }
  }, [featureLimit, featureOrigin, featureSymbol, featureMetric, setError]);

  useEffect(() => {
    if (activeTab !== "features") return;
    void fetchFeatures();
  }, [activeTab, fetchFeatures]);

  const fetchEvents = useCallback(async () => {
    setEventsLoading(true);
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (eventSymbol) params.set("symbol", eventSymbol);
      if (includeHistory) params.set("include_history", "true");
      setEvents(await apiGet<EventsPayload>(`/saveticker/events?${params.toString()}`));
    } catch (err) {
      setError(String(err));
      setEvents(null);
    } finally {
      setEventsLoading(false);
    }
  }, [eventSymbol, includeHistory, setError]);

  useEffect(() => {
    if (activeTab !== "disclosures") return;
    void fetchEvents();
  }, [activeTab, fetchEvents]);

  const fetchPipeline = useCallback(async () => {
    setPipelineLoading(true);
    try {
      setPipeline(await apiGet<PipelinePayload>("/saveticker/pipeline?limit=200"));
    } catch (err) {
      setError(String(err));
      setPipeline(null);
    } finally {
      setPipelineLoading(false);
    }
  }, [setError]);

  useEffect(() => {
    if (activeTab !== "pipeline") return;
    void fetchPipeline();
    const timer = setInterval(() => void fetchPipeline(), 60_000);
    return () => clearInterval(timer);
  }, [activeTab, fetchPipeline]);

  const scheduler = status?.scheduler;
  const database = status?.database;
  const instruments = catalog.instruments ?? [];
  const metrics = catalog.metrics ?? [];
  const collection = collectionState(status);
  const canaryVerdict = canary?.available === false ? "unknown" : (canary?.verdict ?? "unknown");
  const featureAge = ageSeconds(database?.last_feature_observed_at);
  const featureStale = featureAge === null || featureAge > 36 * 3600;

  /* ---------------- columns ---------------- */

  const endpointColumns: Column<EndpointRow>[] = [
    {
      key: "endpoint",
      header: "Endpoint",
      render: (row) => <span className="mono">{row.endpoint}</span>,
      sortValue: (row) => row.endpoint,
    },
    {
      key: "rate",
      header: "성공률",
      width: 180,
      render: (row) => {
        const rate = row.total > 0 ? (row.ok_count / row.total) * 100 : 0;
        const tone = rate >= 99 ? "var(--green)" : rate >= 90 ? "var(--yellow)" : "var(--red)";
        return (
          <div className="ui-rate">
            <div className="ui-rate-track">
              <div className="ui-rate-fill" style={{ width: `${rate}%`, background: tone }} />
            </div>
            <span style={{ color: tone, fontWeight: 700 }}>{rate.toFixed(0)}%</span>
          </div>
        );
      },
      sortValue: (row) => (row.total > 0 ? row.ok_count / row.total : 0),
    },
    {
      key: "ok",
      header: "성공/전체",
      align: "right",
      render: (row) => (
        <span className="tabular-nums">
          {row.ok_count}/{row.total}
        </span>
      ),
      sortValue: (row) => row.total,
    },
    {
      key: "last",
      header: "마지막 시도",
      render: (row) => (
        <span title={fmtKst(row.last_attempt)}>{fmtRelative(row.last_attempt)}</span>
      ),
      sortValue: (row) => row.last_attempt ?? "",
    },
  ];

  const instrumentColumns: Column<Instrument>[] = [
    {
      key: "symbol",
      header: "Symbol",
      render: (row) => (
        <div className="ui-symbol-cell">
          <strong>{row.symbol}</strong>
          {row.display_name ? <span className="muted-note">{row.display_name}</span> : null}
        </div>
      ),
      sortValue: (row) => row.symbol,
    },
    {
      key: "type",
      header: "유형",
      render: (row) => row.instrument_type ?? "—",
      sortValue: (row) => row.instrument_type ?? "",
    },
    {
      key: "related",
      header: "기초자산",
      render: (row) => row.related_perp ?? "—",
      sortValue: (row) => row.related_perp ?? "",
    },
    {
      key: "mapping",
      header: "매핑",
      render: (row) => (
        <StatusChip tone={row.mapping_status === "verified" ? "ok" : "warn"}>
          {labelOf(MAPPING_STATUS_LABELS, row.mapping_status, "미상")}
        </StatusChip>
      ),
      sortValue: (row) => row.mapping_status ?? "",
    },
  ];

  const featureColumns: Column<FeatureRow>[] = [
    {
      key: "instrument",
      header: "종목",
      width: 90,
      render: (row) => <strong>{row.instrument}</strong>,
      sortValue: (row) => row.instrument,
    },
    {
      key: "metric",
      header: "지표",
      render: (row) => (
        <div className="ui-metric-cell">
          <span>{labelOf(METRIC_LABELS, row.metric)}</span>
          <span className="ui-metric-raw">{row.metric}</span>
        </div>
      ),
      sortValue: (row) => row.metric,
    },
    {
      key: "value",
      header: "값",
      align: "right",
      render: (row) => {
        const value = row.value_numeric ?? row.value_float;
        return <span className="tabular-nums">{fmtValue(value, { unit: row.unit })}</span>;
      },
      sortValue: (row) => row.value_numeric ?? row.value_float ?? null,
    },
    {
      key: "flags",
      header: "품질",
      render: (row) => {
        const flags = row.quality_flags ?? [];
        if (flags.length === 0) return <span className="muted-note">정상</span>;
        return (
          <span className="ui-flag-row">
            {flags.map((flag) => (
              <StatusChip
                key={flag}
                tone="warn"
                title={flag}
                icon={<AlertTriangle size={10} />}
              >
                {labelOf(QUALITY_FLAG_LABELS, flag, flag)}
              </StatusChip>
            ))}
          </span>
        );
      },
      sortValue: (row) => (row.quality_flags ?? []).join(","),
    },
    {
      key: "field_as_of",
      header: "기준시각",
      render: (row) => {
        const age = ageSeconds(row.field_as_of);
        const stale = age === null || age > 36 * 3600;
        return (
          <span
            className={stale ? "text-warn" : undefined}
            title={`field_as_of: ${fmtKst(row.field_as_of)}`}
          >
            {fmtRelative(row.field_as_of)}
          </span>
        );
      },
      sortValue: (row) => row.field_as_of ?? "",
    },
    {
      key: "origin",
      header: "출처",
      render: (row) => (
        <span className="ui-origin-cell">
          <StatusChip tone={row.origin === "attachment" ? "purple" : "info"}>
            {labelOf(ORIGIN_LABELS, row.origin, row.origin ?? "—")}
          </StatusChip>
          <span className="muted-note">{row.endpoint ?? "—"}</span>
        </span>
      ),
      sortValue: (row) => row.endpoint ?? "",
    },
  ];

  const eventColumns: Column<TickerEventRow>[] = [
    {
      key: "event_at",
      header: "발생",
      render: (row) => (
        <span title={fmtKst(row.event_at)}>{fmtRelative(row.event_at)}</span>
      ),
      sortValue: (row) => row.event_at ?? "",
    },
    {
      key: "symbol",
      header: "종목",
      render: (row) => <strong>{row.symbol ?? row.instrument ?? "—"}</strong>,
      sortValue: (row) => row.symbol ?? "",
    },
    {
      key: "type",
      header: "유형",
      render: (row) => (
        <StatusChip tone="info">{labelOf(EVENT_TYPE_LABELS, row.event_type, row.event_type ?? "—")}</StatusChip>
      ),
      sortValue: (row) => row.event_type ?? "",
    },
    {
      key: "provider",
      header: "Provider ID",
      render: (row) => (
        <span className="mono muted-note">{row.provider_event_id ?? row.source_id ?? "—"}</span>
      ),
      sortValue: (row) => row.provider_event_id ?? "",
    },
    {
      key: "expires",
      header: "만료",
      render: (row) => <span className="muted-note">{fmtKstShort(row.expires_at)}</span>,
      sortValue: (row) => row.expires_at ?? "",
    },
  ];

  const filingColumns: Column<FilingRow>[] = [
    {
      key: "filed_at",
      header: "공시일",
      render: (row) => (
        <span title={fmtKst(row.filed_at)}>{fmtRelative(row.filed_at)}</span>
      ),
      sortValue: (row) => row.filed_at ?? "",
    },
    {
      key: "symbol",
      header: "종목",
      render: (row) => <strong>{row.symbol ?? "—"}</strong>,
      sortValue: (row) => row.symbol ?? "",
    },
    {
      key: "form",
      header: "Form",
      render: (row) => (
        <StatusChip tone="neutral" title={row.form ?? undefined}>
          {labelOf(FORM_LABELS, row.form, row.form ?? "—")}
        </StatusChip>
      ),
      sortValue: (row) => row.form ?? "",
    },
    {
      key: "title",
      header: "제목",
      render: (row) => <span className="ui-title-cell">{row.title ?? "—"}</span>,
      sortValue: (row) => row.title ?? "",
    },
    {
      key: "content",
      header: "본문",
      render: (row) =>
        row.is_content ? (
          <StatusChip tone="ok">본문 포함</StatusChip>
        ) : (
          <StatusChip tone="warn">헤드라인</StatusChip>
        ),
      sortValue: (row) => (row.is_content ? 1 : 0),
    },
  ];

  const insiderColumns: Column<InsiderRow>[] = [
    {
      key: "transaction_date",
      header: "거래일",
      render: (row) => (
        <span title={fmtKst(row.observed_at)}>
          {row.transaction_date ?? "—"}
        </span>
      ),
      sortValue: (row) => row.transaction_date ?? "",
    },
    {
      key: "symbol",
      header: "종목",
      render: (row) => <strong>{row.symbol ?? "—"}</strong>,
      sortValue: (row) => row.symbol ?? "",
    },
    {
      key: "person",
      header: "인물",
      render: (row) => (
        <div className="ui-person-cell">
          <span>{row.person_name ?? "—"}</span>
          {row.person_title ? <span className="muted-note">{row.person_title}</span> : null}
        </div>
      ),
      sortValue: (row) => row.person_name ?? "",
    },
    {
      key: "code",
      header: "구분",
      render: (row) => (
        <StatusChip tone={row.transaction_code === "S" ? "danger" : row.transaction_code === "P" ? "ok" : "neutral"}>
          {labelOf(INSIDER_CODE_LABELS, row.transaction_code, row.transaction_code ?? "—")}
        </StatusChip>
      ),
      sortValue: (row) => row.transaction_code ?? "",
    },
    {
      key: "value",
      header: "금액",
      align: "right",
      render: (row) => (
        <span
          className="tabular-nums"
          title={row.currency ? undefined : "통화 미검증(미국 SEC Form 4, USD 추정)"}
        >
          {fmtValue(row.signed_value, { unit: row.currency ?? "USD*" })}
        </span>
      ),
      sortValue: (row) => row.signed_value ?? null,
    },
  ];

  const outboxColumns: Column<PipelineRow>[] = [
    {
      key: "created_at",
      header: "생성",
      render: (row) => <span title={fmtKst(row.created_at)}>{fmtRelative(row.created_at)}</span>,
      sortValue: (row) => row.created_at ?? "",
    },
    {
      key: "event_id",
      header: "Event",
      render: (row) => (
        <span className="mono ui-id-cell" title={row.event_id ?? ""}>
          {row.event_id ?? "—"}
        </span>
      ),
      sortValue: (row) => row.event_id ?? "",
    },
    {
      key: "revision",
      header: "Rev",
      align: "right",
      render: (row) => <span className="tabular-nums">r{row.input_revision ?? 0}</span>,
      sortValue: (row) => row.input_revision ?? 0,
    },
    {
      key: "state",
      header: "상태",
      render: (row) => (
        <StatusChip
          tone={row.state === "published" ? "ok" : row.state === "failed" ? "danger" : "warn"}
        >
          {labelOf(JOB_STATE_LABELS, row.state, row.state ?? "—")}
        </StatusChip>
      ),
      sortValue: (row) => row.state ?? "",
    },
    {
      key: "attempts",
      header: "시도",
      align: "right",
      render: (row) => <span className="tabular-nums">{row.attempts ?? 0}</span>,
      sortValue: (row) => row.attempts ?? 0,
    },
    {
      key: "error",
      header: "오류",
      render: (row) =>
        row.last_error ? (
          <span className="text-danger" title={row.last_error}>
            {row.last_error}
          </span>
        ) : (
          <span className="muted-note">—</span>
        ),
      sortValue: (row) => row.last_error ?? "",
    },
  ];

  const jobColumns: Column<PipelineRow>[] = [
    {
      key: "updated_at",
      header: "갱신",
      render: (row) => <span title={fmtKst(row.updated_at)}>{fmtRelative(row.updated_at)}</span>,
      sortValue: (row) => row.updated_at ?? "",
    },
    {
      key: "event_id",
      header: "Event",
      render: (row) => (
        <span className="mono ui-id-cell" title={row.event_id ?? ""}>
          {row.event_id ?? "—"}
        </span>
      ),
      sortValue: (row) => row.event_id ?? "",
    },
    {
      key: "revision",
      header: "Rev",
      align: "right",
      render: (row) => <span className="tabular-nums">r{row.input_revision ?? 0}</span>,
      sortValue: (row) => row.input_revision ?? 0,
    },
    {
      key: "scorer",
      header: "Scorer",
      render: (row) => <span className="muted-note">{row.scorer_version ?? "—"}</span>,
      sortValue: (row) => row.scorer_version ?? "",
    },
    {
      key: "state",
      header: "상태",
      render: (row) => (
        <StatusChip
          tone={row.state === "scored" ? "ok" : row.state === "failed" ? "danger" : "warn"}
        >
          {labelOf(JOB_STATE_LABELS, row.state, row.state ?? "—")}
        </StatusChip>
      ),
      sortValue: (row) => row.state ?? "",
    },
    {
      key: "attempts",
      header: "시도",
      align: "right",
      render: (row) => <span className="tabular-nums">{row.attempts ?? 0}</span>,
      sortValue: (row) => row.attempts ?? 0,
    },
    {
      key: "error",
      header: "오류",
      render: (row) =>
        row.last_error ? (
          <span className="text-danger" title={row.last_error}>
            {row.last_error}
          </span>
        ) : (
          <span className="muted-note">—</span>
        ),
      sortValue: (row) => row.last_error ?? "",
    },
  ];

  const revisionColumns: Column<PipelineRow>[] = [
    {
      key: "first_seen_at",
      header: "발견",
      render: (row) => (
        <span title={fmtKst(row.first_seen_at)}>{fmtRelative(row.first_seen_at)}</span>
      ),
      sortValue: (row) => row.first_seen_at ?? "",
    },
    {
      key: "event_id",
      header: "Event",
      render: (row) => (
        <span className="mono ui-id-cell" title={row.event_id ?? ""}>
          {row.event_id ?? "—"}
        </span>
      ),
      sortValue: (row) => row.event_id ?? "",
    },
    {
      key: "revision",
      header: "Rev",
      align: "right",
      render: (row) => (
        <StatusChip tone={(row.revision ?? 1) > 1 ? "warn" : "neutral"}>r{row.revision ?? 1}</StatusChip>
      ),
      sortValue: (row) => row.revision ?? 0,
    },
    {
      key: "title",
      header: "제목",
      render: (row) => <span className="ui-title-cell">{row.title ?? "—"}</span>,
      sortValue: (row) => row.title ?? "",
    },
    {
      key: "provider",
      header: "Provider",
      render: (row) => <span className="muted-note">{row.provider ?? "—"}</span>,
      sortValue: (row) => row.provider ?? "",
    },
  ];

  const outboxRows = useMemo(() => {
    const rows = pipeline?.outbox ?? [];
    return onlyProblems ? rows.filter((row) => row.state !== "published") : rows;
  }, [pipeline, onlyProblems]);

  const jobRows = useMemo(() => {
    const rows = pipeline?.jobs ?? [];
    return onlyProblems ? rows.filter((row) => row.state !== "scored") : rows;
  }, [pipeline, onlyProblems]);

  return (
    <div className="page">
      <PageHeader
        icon={<Database size={24} color="var(--accent)" />}
        title="SaveTicker 데이터 관제"
        description="실시간 종목 피처 · 공시/이벤트/내부자 · 뉴스 리비전 파이프라인 · 수집 canary gate를 한 화면에서 확인합니다. 조회 전용이며 원격 SaveTicker 호출은 발생하지 않습니다."
        actions={
          <button className="btn-secondary" onClick={() => wrap(fetchStatus)} disabled={statusLoading}>
            <RefreshCw size={14} className={statusLoading ? "spin" : ""} />
            새로고침
          </button>
        }
      />

      <ErrorBanner message={error} onDismiss={() => setError(null)} />

      {/* Health strip */}
      <div className="grid grid-6" style={{ marginBottom: "var(--space-4)" }}>
        <StatTile
          label="수집 상태"
          value={collection.text}
          sub={collection.detail}
          tone={collection.tone === "ok" ? "ok" : collection.tone === "neutral" ? undefined : collection.tone}
          icon={
            collection.tone === "ok" ? (
              <CheckCircle2 size={15} color="var(--green)" />
            ) : (
              <AlertTriangle size={15} color="var(--yellow)" />
            )
          }
          compactValue
        />
        <StatTile
          label="Live Feature 신선도"
          value={fmtRelative(database?.last_feature_observed_at)}
          sub={fmtKst(database?.last_feature_observed_at)}
          tone={featureStale ? "warn" : "ok"}
          icon={<LineChart size={15} color="var(--accent)" />}
          compactValue
        />
        <StatTile
          label={`Raw · ${canary?.window_hours ?? 24}h`}
          value={fmtCompact(canary?.raw?.recent ?? 0, 0)}
          sub={`전체 ${fmtCompact(database?.raw_storage?.rows ?? 0, 0)}행 · ${fmtBytes(
            database?.raw_storage?.total_bytes,
          )}`}
          icon={<Database size={15} color="var(--yellow)" />}
        />
        <StatTile
          label={`Feature · ${canary?.window_hours ?? 24}h`}
          value={fmtCompact(canary?.features?.recent ?? 0, 0)}
          sub={`${canary?.features?.instruments ?? 0}종목 · universe ${instruments.length}종목`}
          icon={<Activity size={15} color="var(--purple)" />}
        />
        <StatTile
          label="Outbox / Score"
          value={`${database?.outbox?.pending ?? 0} / ${database?.score_jobs?.pending ?? 0}`}
          sub={`실패 ${database?.outbox?.failed ?? 0} · ${database?.score_jobs?.failed ?? 0} · 완료 ${
            database?.score_jobs?.scored ?? 0
          }`}
          tone={
            (database?.outbox?.failed ?? 0) + (database?.score_jobs?.failed ?? 0) > 0
              ? "danger"
              : "ok"
          }
          icon={<GitBranch size={15} color="var(--green)" />}
        />
        <StatTile
          label="Core 지연"
          value={canary?.core?.md_ohlcv_lag_seconds != null ? `${canary.core.md_ohlcv_lag_seconds}s` : "—"}
          sub={
            canary?.core?.s1_equity != null
              ? `S1 equity ${fmtValue(canary.core.s1_equity, { digits: 2 })} · ${
                  canary.core.s1_position ?? "—"
                }`
              : "S1 스냅샷 없음"
          }
          tone={
            canary?.core?.md_ohlcv_lag_seconds != null && canary.core.md_ohlcv_lag_seconds < 180
              ? "ok"
              : "warn"
          }
          icon={<Activity size={15} color="var(--red)" />}
        />
      </div>

      <div className="subtabs" role="tablist" aria-label="SaveTicker 보기 선택">
        {(
          [
            { key: "status", label: "수집 상태", icon: <Activity size={14} /> },
            { key: "features", label: "피처 탐색기", icon: <LineChart size={14} /> },
            { key: "disclosures", label: "공시·이벤트·내부자", icon: <FileText size={14} /> },
            { key: "pipeline", label: "뉴스 파이프라인", icon: <GitBranch size={14} /> },
          ] as Array<{ key: TabKey; label: string; icon: ReactNode }>
        ).map(({ key, label, icon }) => (
          <button
            key={key}
            role="tab"
            aria-selected={activeTab === key}
            className={`subtab ${activeTab === key ? "active" : ""}`}
            onClick={() => setActiveTab(key)}
          >
            {icon}
            <span>{label}</span>
          </button>
        ))}
      </div>

      {activeTab === "status" && (
        <>
          <div className="card" style={{ marginBottom: "var(--space-4)" }}>
            <div className="card-title-row">
              <div className="card-title">
                Canary Gate · 최근 {canary?.window_hours ?? 24}시간
                <InfoTip text="운영 runbook의 7개 수집 건강 조건을 자동 판정합니다. 각 조건에 마우스를 올리면 설명이 표시됩니다." />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <StatusChip
                  tone={canaryVerdict === "pass" ? "ok" : canaryVerdict === "fail" ? "danger" : "warn"}
                  icon={
                    canaryVerdict === "pass" ? (
                      <CheckCircle2 size={11} />
                    ) : (
                      <XCircle size={11} />
                    )
                  }
                >
                  {canaryVerdict === "pass" ? "정상" : canaryVerdict === "fail" ? "실패" : "판정 불가"}
                </StatusChip>
                <span className="muted-note">기준 {fmtKst(canary?.generated_at)}</span>
              </div>
            </div>
            {canary?.available === false && (
              <div className="notice-card" style={{ marginBottom: "var(--space-3)" }}>
                canary 조회 불가: {canary.error ?? "unknown"}
              </div>
            )}
            <div className="ui-gate-grid">
              {(canary?.checks ?? []).map((check) => {
                const meta = gateLabel(check.name);
                return (
                  <div
                    key={check.name}
                    className={`ui-gate ${check.passed ? "pass" : "fail"}`}
                    title={meta.desc}
                  >
                    {check.passed ? (
                      <CheckCircle2 size={14} color="var(--green)" />
                    ) : (
                      <XCircle size={14} color="var(--red)" />
                    )}
                    <div>
                      <div className="ui-gate-label">{meta.label}</div>
                      <div className="ui-gate-desc">{meta.desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="card" style={{ marginBottom: "var(--space-4)" }}>
            <div className="card-title-row">
              <div className="card-title">수집 플래그 · Universe</div>
              <span className="card-title-meta">
                {scheduler?.degraded ? `저하: ${scheduler.degraded_reason}` : "정상"} ·{" "}
                {fmtKst(scheduler?.updated_at)}
              </span>
            </div>
            <div className="ui-flag-row" style={{ marginBottom: "var(--space-3)" }}>
              <StatusChip tone={scheduler?.outbox_enabled ? "ok" : "neutral"}>
                Outbox {scheduler?.outbox_enabled ? "ON" : "OFF"}
              </StatusChip>
              <StatusChip tone={scheduler?.input_revision_enabled ? "ok" : "neutral"}>
                Revision {scheduler?.input_revision_enabled ? "ON" : "OFF"}
              </StatusChip>
              <StatusChip tone={scheduler?.market_data_enabled ? "ok" : "neutral"}>
                Market {scheduler?.market_data_enabled ? "ON" : "OFF"}
              </StatusChip>
              <StatusChip tone={scheduler?.company_news_enabled ? "ok" : "neutral"}>
                Company News {scheduler?.company_news_enabled ? "ON" : "OFF"}
              </StatusChip>
              <StatusChip tone={scheduler?.eod_enabled ? "ok" : "neutral"}>
                EOD {scheduler?.eod_enabled ? "ON" : "OFF"}
              </StatusChip>
              {scheduler?.auth_cooldown_sec != null && (
                <StatusChip tone="info">Auth cooldown {scheduler.auth_cooldown_sec}s</StatusChip>
              )}
            </div>
            <div className="muted-note">
              Universe: {scheduler?.universe?.source ?? "unknown"} (
              {scheduler?.universe?.symbols?.length ?? 0}종목)
              {scheduler?.universe?.degraded_reason
                ? ` · 저하: ${scheduler.universe.degraded_reason}`
                : ""}
              {scheduler?.eod_done_sessions?.length
                ? ` · EOD 완료 세션 ${scheduler.eod_done_sessions.join(", ")}`
                : ""}
              {scheduler?.enabled_endpoints?.length
                ? ` · 활성 endpoint ${scheduler.enabled_endpoints.length}종`
                : ""}
            </div>
          </div>

          <div className="grid grid-2">
            <div className="card">
              <div className="card-title-row">
                <div className="card-title">Endpoint 수집 품질 (7일)</div>
                <span className="card-title-meta">{fmtKst(status?.collected_at)}</span>
              </div>
              <DataTable
                columns={endpointColumns}
                rows={database?.endpoints ?? []}
                rowKey={(row) => row.endpoint}
                pageSize={11}
                dense
                emptyText="수집 기록이 없습니다."
              />
            </div>

            <div className="card">
              <div className="card-title-row">
                <div className="card-title">
                  <Building2 size={15} /> 종목 Universe
                </div>
                <span className="card-title-meta">{instruments.length}종목</span>
              </div>
              {instruments.length === 0 ? (
                <EmptyState
                  icon={<Building2 size={22} />}
                  title="instrument registry가 비어 있습니다"
                  description="koru-core 재시작 후 live universe가 등록되면 채워집니다. 값이 계속 비어 있으면 storage 초기화 실패 로그를 확인하세요."
                />
              ) : (
                <DataTable
                  columns={instrumentColumns}
                  rows={instruments}
                  rowKey={(row) => row.symbol}
                  searchable
                  searchPlaceholder="종목 검색..."
                  searchText={(row) => `${row.symbol} ${row.display_name ?? ""}`}
                  pageSize={11}
                  dense
                />
              )}
            </div>
          </div>
        </>
      )}

      {activeTab === "features" && (
        <div className="card">
          <div className="card-title-row">
            <div className="card-title">
              <LineChart size={16} /> 정규화 피처 탐색기
            </div>
            <span className="card-title-meta">
              {features.length}건 · 기준 {fmtKst(database?.last_feature_observed_at)}
            </span>
          </div>

          <div className="ui-filterbar">
            <select value={featureSymbol} onChange={(event) => setFeatureSymbol(event.target.value)}>
              <option value="">전체 종목</option>
              {instruments.map((instrument) => (
                <option key={instrument.symbol} value={instrument.symbol}>
                  {instrument.symbol}
                </option>
              ))}
            </select>
            <select value={featureMetric} onChange={(event) => setFeatureMetric(event.target.value)}>
              <option value="">전체 지표</option>
              {metrics.map((metric) => (
                <option key={metric.metric} value={metric.metric}>
                  {labelOf(METRIC_LABELS, metric.metric)}
                  {metric.unit ? ` (${metric.unit})` : ""}
                </option>
              ))}
            </select>
            <select value={featureOrigin} onChange={(event) => setFeatureOrigin(event.target.value)}>
              {ORIGIN_OPTIONS.map((origin) => (
                <option key={origin} value={origin}>
                  {labelOf(ORIGIN_LABELS, origin, origin)}
                </option>
              ))}
            </select>
            <select value={featureLimit} onChange={(event) => setFeatureLimit(Number(event.target.value))}>
              <option value={100}>100건</option>
              <option value={200}>200건</option>
              <option value={500}>500건</option>
            </select>
            <button className="btn-secondary" onClick={() => wrap(fetchFeatures)} disabled={featuresLoading}>
              <RefreshCw size={14} className={featuresLoading ? "spin" : ""} />
              조회
            </button>
          </div>

          <DataTable
            columns={featureColumns}
            rows={features}
            rowKey={(row) => `${row.observation_id ?? `${row.instrument}:${row.metric}:${row.observed_at}`}`}
            loading={featuresLoading}
            searchable
            searchPlaceholder="종목 · 지표 검색..."
            searchText={(row) => `${row.instrument} ${row.metric} ${labelOf(METRIC_LABELS, row.metric)}`}
            pageSize={25}
            dense
            onRowClick={(row) => setFeatureDetail(row)}
            emptyText="조건에 맞는 피처가 없습니다. 첨부 import 자료는 출처를 '첨부 import'로 변경하세요."
            footerNote="행을 클릭하면 lineage 상세가 열립니다."
          />

          <Drawer
            open={featureDetail !== null}
            title={featureDetail ? `${featureDetail.instrument} · ${labelOf(METRIC_LABELS, featureDetail.metric)}` : ""}
            subtitle={featureDetail ? `${featureDetail.metric} · ${featureDetail.endpoint ?? "—"}` : undefined}
            onClose={() => setFeatureDetail(null)}
          >
            {featureDetail && (
              <DetailList
                items={[
                  ["값", fmtValue(featureDetail.value_numeric ?? featureDetail.value_float, { unit: featureDetail.unit })],
                  ["단위", featureDetail.unit ?? "—"],
                  ["관측시각", fmtKst(featureDetail.observed_at)],
                  ["기준시각(field_as_of)", `${fmtKst(featureDetail.field_as_of)} (${fmtRelative(featureDetail.field_as_of)})`],
                  ["기준시각 원문", featureDetail.field_as_of_raw ?? "—"],
                  ["출처", `${labelOf(ORIGIN_LABELS, featureDetail.origin, featureDetail.origin ?? "—")} · ${featureDetail.endpoint ?? "—"}`],
                  [
                    "품질 플래그",
                    (featureDetail.quality_flags ?? []).length > 0
                      ? (featureDetail.quality_flags ?? [])
                          .map((flag) => labelOf(QUALITY_FLAG_LABELS, flag, flag))
                          .join(", ")
                      : "정상",
                  ],
                  ["observation_id", <span className="mono">{featureDetail.observation_id ?? "—"}</span>],
                ]}
              />
            )}
          </Drawer>
        </div>
      )}

      {activeTab === "disclosures" && (
        <>
          <div className="card" style={{ marginBottom: "var(--space-4)" }}>
            <div className="card-title-row">
              <div className="card-title">
                <FileText size={16} /> 공시 · 이벤트 조회
                <InfoTip text="원문 JSON payload는 노출하지 않습니다. 공시/내부자는 같은 내용이 snapshot마다 중복 저장되므로 기본 조회는 최신 1건만 표시합니다." />
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <select value={eventSymbol} onChange={(event) => setEventSymbol(event.target.value)}>
                  <option value="">전체 종목</option>
                  {instruments.map((instrument) => (
                    <option key={instrument.symbol} value={instrument.symbol}>
                      {instrument.symbol}
                    </option>
                  ))}
                </select>
                <label className="ui-toggle">
                  <input
                    type="checkbox"
                    checked={includeHistory}
                    onChange={(event) => setIncludeHistory(event.target.checked)}
                  />
                  <span>전체 이력(중복 포함)</span>
                </label>
                <button className="btn-secondary" onClick={() => wrap(fetchEvents)} disabled={eventsLoading}>
                  <RefreshCw size={14} className={eventsLoading ? "spin" : ""} />
                  조회
                </button>
              </div>
            </div>
          </div>

          <div className="card" style={{ marginBottom: "var(--space-4)" }}>
            <div className="card-title-row">
              <div className="card-title">
                <Activity size={15} /> Ticker Events
              </div>
              <span className="card-title-meta">{events?.events?.length ?? 0}건</span>
            </div>
            <DataTable
              columns={eventColumns}
              rows={events?.events ?? []}
              rowKey={(row) => row.event_id ?? `${row.symbol}-${row.provider_event_id}`}
              loading={eventsLoading}
              searchable
              searchPlaceholder="종목 · 유형 검색..."
              searchText={(row) => `${row.symbol ?? ""} ${row.event_type ?? ""} ${row.provider_event_id ?? ""}`}
              pageSize={15}
              dense
              emptyText="이벤트가 없습니다."
            />
          </div>

          <div className="card" style={{ marginBottom: "var(--space-4)" }}>
            <div className="card-title-row">
              <div className="card-title">
                <FileText size={15} /> Filings (공시)
              </div>
              <span className="card-title-meta">
                {events?.filings?.length ?? 0}건 {includeHistory ? "· 전체 이력" : "· 중복 제거"}
              </span>
            </div>
            <DataTable
              columns={filingColumns}
              rows={events?.filings ?? []}
              rowKey={(row) => row.revision_id ?? `${row.symbol}-${row.provider_filing_id}`}
              loading={eventsLoading}
              searchable
              searchPlaceholder="종목 · 제목 · Form 검색..."
              searchText={(row) =>
                `${row.symbol ?? ""} ${row.title ?? ""} ${row.form ?? ""} ${row.category ?? ""}`
              }
              pageSize={15}
              dense
              onRowClick={(row) => setFilingDetail(row)}
              emptyText="공시가 없습니다."
              footerNote="행을 클릭하면 상세가 열립니다."
            />
          </div>

          <div className="card">
            <div className="card-title-row">
              <div className="card-title">
                <Users size={15} /> Insider Trades (내부자 거래)
              </div>
              <span className="card-title-meta">
                {events?.insider_trades?.length ?? 0}건 {includeHistory ? "· 전체 이력" : "· 중복 제거"}
              </span>
            </div>
            <DataTable
              columns={insiderColumns}
              rows={events?.insider_trades ?? []}
              rowKey={(row) =>
                row.observation_id ?? `${row.symbol}-${row.person_name}-${row.transaction_date}`
              }
              loading={eventsLoading}
              searchable
              searchPlaceholder="종목 · 인물 검색..."
              searchText={(row) =>
                `${row.symbol ?? ""} ${row.person_name ?? ""} ${row.person_title ?? ""} ${row.transaction_code ?? ""}`
              }
              pageSize={15}
              dense
              emptyText="내부자 거래 관측이 없습니다."
              footerNote="* 통화는 제공자 미검증(미국 SEC Form 4 기준 USD 추정)입니다."
            />
          </div>

          <Drawer
            open={filingDetail !== null}
            title={filingDetail ? `${filingDetail.symbol ?? "—"} · ${filingDetail.form ?? "—"}` : ""}
            subtitle={filingDetail?.title ?? undefined}
            onClose={() => setFilingDetail(null)}
          >
            {filingDetail && (
              <DetailList
                items={[
                  ["공시일", fmtKst(filingDetail.filed_at)],
                  ["관측시각", fmtKst(filingDetail.observed_at)],
                  ["Form", labelOf(FORM_LABELS, filingDetail.form, filingDetail.form ?? "—")],
                  ["카테고리", labelOf(FILING_CATEGORY_LABELS, filingDetail.category, filingDetail.category ?? "—")],
                  ["본문 포함", filingDetail.is_content ? "예" : "아니오(헤드라인)"],
                  ["provider_filing_id", <span className="mono">{filingDetail.provider_filing_id ?? "—"}</span>],
                  ["revision_id", <span className="mono">{filingDetail.revision_id ?? "—"}</span>],
                ]}
              />
            )}
          </Drawer>
        </>
      )}

      {activeTab === "pipeline" && (
        <>
          {pipeline?.available === false && (
            <div className="notice-card" style={{ marginBottom: "var(--space-4)" }}>
              파이프라인 테이블 조회 불가: {pipeline.error ?? "schema_or_db_unavailable"}
            </div>
          )}

          <div className="grid grid-4" style={{ marginBottom: "var(--space-4)" }}>
            <StatTile
              label="Outbox"
              value={`${pipeline?.outbox?.length ?? 0}`}
              sub={`실패 ${(pipeline?.outbox ?? []).filter((row) => row.state === "failed").length}건`}
              tone={
                (pipeline?.outbox ?? []).some((row) => row.state === "failed") ? "danger" : "ok"
              }
              icon={<GitBranch size={15} color="var(--green)" />}
            />
            <StatTile
              label="Score Jobs"
              value={`${pipeline?.jobs?.length ?? 0}`}
              sub={`미완료 ${(pipeline?.jobs ?? []).filter((row) => row.state !== "scored").length}건`}
              tone={(pipeline?.jobs ?? []).some((row) => row.state === "failed") ? "danger" : "ok"}
              icon={<Activity size={15} color="var(--orange)" />}
            />
            <StatTile
              label="입력 리비전"
              value={`${pipeline?.revisions?.length ?? 0}`}
              sub={`r2+ ${(pipeline?.revisions ?? []).filter((row) => (row.revision ?? 1) > 1).length}건`}
              icon={<FileText size={15} color="var(--accent)" />}
            />
            <StatTile
              label="최근 발행"
              value={fmtRelative(database?.outbox?.last_published)}
              sub={fmtKst(database?.outbox?.last_published)}
              icon={<CheckCircle2 size={15} color="var(--green)" />}
              compactValue
            />
          </div>

          <div className="card" style={{ marginBottom: "var(--space-4)" }}>
            <div className="card-title-row">
              <div className="card-title">
                <GitBranch size={15} /> Publish Outbox
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <label className="ui-toggle">
                  <input
                    type="checkbox"
                    checked={onlyProblems}
                    onChange={(event) => setOnlyProblems(event.target.checked)}
                  />
                  <span>문제만 보기</span>
                </label>
                <button className="btn-secondary" onClick={() => wrap(fetchPipeline)} disabled={pipelineLoading}>
                  <RefreshCw size={13} className={pipelineLoading ? "spin" : ""} />
                </button>
              </div>
            </div>
            <DataTable
              columns={outboxColumns}
              rows={outboxRows}
              rowKey={(row) => `out-${row.outbox_id ?? row.event_id ?? ""}`}
              loading={pipelineLoading}
              searchable
              searchPlaceholder="event id 검색..."
              searchText={(row) => `${row.event_id ?? ""} ${row.state ?? ""} ${row.last_error ?? ""}`}
              pageSize={15}
              dense
              onRowClick={(row) => setPipelineDetail({ kind: "outbox", row })}
              emptyText={onlyProblems ? "문제가 있는 outbox 행이 없습니다." : "outbox 행이 없습니다."}
            />
          </div>

          <div className="card" style={{ marginBottom: "var(--space-4)" }}>
            <div className="card-title-row">
              <div className="card-title">Score Jobs</div>
              <span className="card-title-meta">{jobRows.length}건</span>
            </div>
            <DataTable
              columns={jobColumns}
              rows={jobRows}
              rowKey={(row) => `job-${row.event_id ?? ""}-${row.input_revision ?? 0}`}
              loading={pipelineLoading}
              searchable
              searchPlaceholder="event id · scorer 검색..."
              searchText={(row) => `${row.event_id ?? ""} ${row.scorer_version ?? ""} ${row.state ?? ""}`}
              pageSize={15}
              dense
              onRowClick={(row) => setPipelineDetail({ kind: "job", row })}
              emptyText={onlyProblems ? "미완료/실패 job이 없습니다." : "score job 행이 없습니다."}
            />
          </div>

          <div className="card">
            <div className="card-title-row">
              <div className="card-title">최근 입력 리비전</div>
              <span className="card-title-meta">{pipeline?.revisions?.length ?? 0}건</span>
            </div>
            <DataTable
              columns={revisionColumns}
              rows={pipeline?.revisions ?? []}
              rowKey={(row) => `rev-${row.event_id ?? ""}-${row.revision ?? 0}`}
              loading={pipelineLoading}
              searchable
              searchPlaceholder="제목 · provider 검색..."
              searchText={(row) => `${row.title ?? ""} ${row.provider ?? ""} ${row.event_id ?? ""}`}
              pageSize={15}
              dense
              onRowClick={(row) => setPipelineDetail({ kind: "revision", row })}
              emptyText="리비전 행이 없습니다."
            />
          </div>

          <Drawer
            open={pipelineDetail !== null}
            title={
              pipelineDetail
                ? pipelineDetail.kind === "outbox"
                  ? "Outbox 상세"
                  : pipelineDetail.kind === "job"
                    ? "Score Job 상세"
                    : "입력 리비전 상세"
                : ""
            }
            subtitle={pipelineDetail?.row.title ?? pipelineDetail?.row.event_id ?? undefined}
            onClose={() => setPipelineDetail(null)}
          >
            {pipelineDetail && (
              <DetailList
                items={[
                  ["event_id", <span className="mono">{pipelineDetail.row.event_id ?? "—"}</span>],
                  ...(pipelineDetail.row.event_id
                    ? ([
                        [
                          "복사",
                          <CopyButton value={pipelineDetail.row.event_id} label="event_id" />,
                        ],
                      ] as Array<[string, ReactNode]>)
                    : []),
                  ["상태", labelOf(JOB_STATE_LABELS, pipelineDetail.row.state, pipelineDetail.row.state ?? "—")],
                  ["리비전", `r${pipelineDetail.row.input_revision ?? pipelineDetail.row.revision ?? 0}`],
                  ["시도", `${pipelineDetail.row.attempts ?? 0}회`],
                  ["생성", fmtKst(pipelineDetail.row.created_at)],
                  ["갱신", fmtKst(pipelineDetail.row.updated_at)],
                  ["발행", fmtKst(pipelineDetail.row.published_at_utc)],
                  ["다음 시도", fmtKst(pipelineDetail.row.next_attempt_at)],
                  ["scorer", pipelineDetail.row.scorer_version ?? "—"],
                  ["provider", pipelineDetail.row.provider ?? "—"],
                  ["provider_news_id", pipelineDetail.row.provider_news_id ?? "—"],
                  ["origin", labelOf(ORIGIN_LABELS, pipelineDetail.row.origin, pipelineDetail.row.origin ?? "—")],
                  ["last_error", pipelineDetail.row.last_error ?? "—"],
                ]}
              />
            )}
          </Drawer>
        </>
      )}
    </div>
  );
}
